import { ActionReducerMapBuilder, createAsyncThunk } from '@reduxjs/toolkit';

import { ThunkApiConfigType } from '../../../../main/store/main.store.ts';
import { AppStateEnum } from '../../../../main/types/app-state.types.ts';

import { getAppStateUtil } from '../../../utils/app-state/app-state.util.ts';
import { getActivityTrackingUtil } from '../../../utils/managers/activity-tracking.util.ts';

import { setCurrentUpdateTimeoutId } from '../app-state.slice.ts';
import { APP_STATE_SLICE_NAME, AppStateSliceState } from '../app-state.slice.types.ts';

interface Props {
  state: AppStateEnum;
  delay?: number;
  blockAnimation?: boolean;
}

interface ReturnType {
  state: AppStateEnum;
  blockAnimation?: boolean;
}

export const updateAppStateThunk = createAsyncThunk<ReturnType | undefined, Props, ThunkApiConfigType>(
  `${APP_STATE_SLICE_NAME}/updateAppStateThunk`,
  async (props, thunkAPI): Promise<ReturnType | undefined> => {
    const { currentUpdateTimeoutId } = thunkAPI.getState().appState;
    if (currentUpdateTimeoutId) {
      clearTimeout(currentUpdateTimeoutId);
      thunkAPI.dispatch(setCurrentUpdateTimeoutId(null));
    }

    const handleUpdate = (resolve: (value: ReturnType | undefined) => void): void => {
      const { state, isAbortingStep } = thunkAPI.getState().appState;
      if (state === props.state) return resolve(undefined);
      if (isAbortingStep && props.state !== AppStateEnum.ReadyToStart) return resolve(undefined);

      if (
        state === AppStateEnum.ReadyToStart &&
        [
          AppStateEnum.Correct,
          AppStateEnum.Incorrect,
          AppStateEnum.AssumeCorrect,
          AppStateEnum.Evaluating,
          AppStateEnum.EvaluatingDrawing,
          AppStateEnum.MoveToNextStep,
          AppStateEnum.ReadyForNextStep,
          AppStateEnum.Retry,
        ].includes(props.state)
      ) {
        return resolve(undefined);
      }
      console.log(`[APP STATE] ${state} -> ${props.state.toString()}`);
      resolve({ state: props.state, blockAnimation: props.blockAnimation ?? false });
      getAppStateUtil().handleAppStateChange(props.state);
      getActivityTrackingUtil().handleAppStateChange(props.state);
    };

    return new Promise((resolve) => {
      if (props.delay) {
        const timeout = setTimeout(() => {
          handleUpdate(resolve);
        }, props.delay);
        thunkAPI.dispatch(setCurrentUpdateTimeoutId(timeout));
      } else {
        handleUpdate(resolve);
      }
    });
  }
);

export const updateAppStateThunkExtraReducers = (builder: ActionReducerMapBuilder<AppStateSliceState>): void => {
  builder.addCase(updateAppStateThunk.fulfilled, (state, action) => {
    if (action.payload) {
      state.state = action.payload.state;
      state.isBlockingAnimation = action.payload.blockAnimation ?? false;
      state.isAbortingStep = action.payload.state === AppStateEnum.Aborting;
    }
  });
};
